import React from 'react';
import { Link } from 'react-router-dom';

function ProfileHeader({ profile, isOwner, isFollowing, onFollow, onMessage }) {
  if (!profile) return null;

  // Normalize field names
  const username = profile.Username || profile.username || '';
  const displayName = profile.DisplayName || profile.display_name || profile.displayName || username;
  const bio = profile.Bio || profile.bio || '';
  const avatar = profile.Avatar || profile.avatar || profile.ProfileImage || profile.profile_image;
  const eventsCount = profile.EventsCount || profile.events_count || profile.eventsCount || 0;
  const followersCount = profile.FollowersCount || profile.followers_count || profile.followersCount || 0;
  const attendingCount = profile.AttendingCount || profile.attending_count || profile.attendingCount || 0;

  // GET /api/users/:id preloads Interests. Nothing rendered them anywhere, so
  // the interests a user picked in EditProfile were write-only.
  const interests = (profile.Interests || profile.interests || []).filter(Boolean);

  const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(
    displayName || username
  )}&size=96&background=E6F7FA&color=4CBED9&bold=true`;

  const avatarSrc = avatar || fallbackAvatar;

  return (
    <div className="bg-white rounded-2xl shadow-soft-sm border border-gray-100 overflow-hidden">
      {/* Banner */}
      <div className="h-32 sm:h-40 bg-gradient-to-r from-primary-400 via-primary-500 to-accent-400" />

      {/* Profile info */}
      <div className="px-4 sm:px-6 pb-5">
        {/* Avatar + Actions row */}
        <div className="flex items-end justify-between -mt-12 mb-4">
          {/* Avatar */}
          <img
            src={avatarSrc}
            alt={`${displayName || username}'s avatar`}
            className="w-24 h-24 rounded-2xl border-4 border-white shadow-soft-md object-cover bg-surface-50"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = fallbackAvatar;
            }}
          />

          {/* Action buttons */}
          <div className="flex items-center gap-2 pb-1">
            {isOwner ? (
              <Link
                to="/edit-profile"
                className="inline-flex items-center gap-2 px-4 py-2 text-body-sm font-semibold text-surface-700 bg-white border border-surface-200 rounded-xl hover:bg-surface-50 focus:outline-none focus:ring-2 focus:ring-primary-500/30 transition-colors duration-200"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                Edit Profile
              </Link>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onFollow}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-body-sm font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30 transition-colors duration-200 ${
                    isFollowing
                      ? 'text-primary-600 bg-white border border-primary-300 hover:bg-primary-50'
                      : 'text-white bg-primary-500 hover:bg-primary-600'
                  }`}
                >
                  {isFollowing ? 'Following' : 'Follow'}
                </button>
                {onMessage && (
                  <button
                    type="button"
                    onClick={onMessage}
                    className="inline-flex items-center gap-2 px-4 py-2 text-body-sm font-semibold text-surface-700 bg-white border border-surface-200 rounded-xl hover:bg-surface-50 focus:outline-none focus:ring-2 focus:ring-primary-500/30 transition-colors duration-200"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                    Message
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Name + Username */}
        <div className="mb-3">
          <h2 className="text-h2 text-surface-900">{displayName}</h2>
          {username && (
            <p className="text-body-sm text-surface-400 mt-0.5">@{username}</p>
          )}
        </div>

        {/* Bio */}
        {bio && (
          <p className="text-body text-surface-600 mb-4 max-w-lg">{bio}</p>
        )}

        {/* Interests */}
        {interests.length > 0 && (
          <ul className="flex flex-wrap gap-2 mb-4" aria-label="Interests">
            {interests.map((interest) => {
              const id = interest.id || interest.ID;
              const name = interest.name || interest.Name;
              if (!name) return null;
              return (
                <li
                  key={id || name}
                  className="px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-body-sm font-medium border border-primary-100"
                >
                  {name}
                </li>
              );
            })}
          </ul>
        )}

        {/* Stats */}
        <div className="flex items-center gap-6 pt-3 border-t border-surface-100">
          <div className="text-center">
            <span className="block text-h3 text-surface-900">{eventsCount}</span>
            <span className="text-caption text-surface-400">Events</span>
          </div>
          <div className="text-center">
            <span className="block text-h3 text-surface-900">{followersCount}</span>
            <span className="text-caption text-surface-400">Followers</span>
          </div>
          <div className="text-center">
            <span className="block text-h3 text-surface-900">{attendingCount}</span>
            <span className="text-caption text-surface-400">Attending</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfileHeader;
